import { useState, useRef } from "react"
import { toast } from "sonner"
import { Download, Upload } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"

export const TransferSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const [importing, setImporting] = useState(false)
    const [exporting, setExporting] = useState(false)
    const importRef = useRef<HTMLInputElement>(null)

    const handleExport = async () => {
        setExporting(true)
        try {
            const res = await fetch(`/api/areals/${areal.id}/export`, { credentials: "include" })
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const blob = await res.blob()
            const disposition = res.headers.get("Content-Disposition") ?? ""
            const match = disposition.match(/filename="([^"]+)"/)
            const filename = match?.[1] ?? `areal-export-${areal.slug}.tar.gz`
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
        } catch {
            toast.error(t("admin.settings.transferExportFailed"))
        } finally {
            setExporting(false)
        }
    }

    const handleImportFile = async (file: File) => {
        setImporting(true)
        try {
            const formData = new FormData()
            formData.append("file", file)
            await api.upload("/areals/import", formData)
            toast.success(t("admin.settings.transferImportSuccess"))
        } catch (err) {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.transferImportFailed"))
        } finally {
            setImporting(false)
            if (importRef.current) importRef.current.value = ""
        }
    }

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionTransfer")}</h2>
            <p className="text-sm font-bold text-slate-500">{t("admin.settings.transferDescription")}</p>
            <div className="flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={exporting}
                    className={cn(primaryBtnClass, "flex items-center gap-2 disabled:opacity-40")}
                >
                    <Download className="size-3.5" />
                    {exporting ? t("admin.settings.transferExporting") : t("admin.settings.transferExportButton")}
                </button>
                <button
                    type="button"
                    onClick={() => importRef.current?.click()}
                    disabled={importing}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-slate-700 text-white font-black text-sm hover:bg-slate-800 transition-colors active:scale-95 disabled:opacity-40 shadow-md"
                >
                    <Upload className="size-3.5" />
                    {importing ? t("admin.settings.transferImporting") : t("admin.settings.transferImportButton")}
                </button>
                <input
                    ref={importRef}
                    type="file"
                    accept=".tar.gz,.zip"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) void handleImportFile(file)
                    }}
                />
            </div>
        </div>
    )
}
